import { AccordionIcon } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-icons/accordion-icon';
import { CalloutIcon } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-icons/callout-icon';
import { ChartIcon } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-icons/chart-icon';
import { ColumnsIcon } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-icons/columns-icon';
import { DataDisplayIcon } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-icons/data-display-icon';
import { DiagramIcon } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-icons/diagram-icon';
import { FormulaIcon } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-icons/formula-icon';
import { GraphpadIcon } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-icons/graphpad-icon';
import { KeyIcon } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-icons/key-icon';
import type { SlashMenuItem } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui/slash-dropdown-menu/use-slash-dropdown-menu';
import { isNodeInSchema } from '@examdojo/core/tiptap-react-ui-components/lib/tiptap-utils';
import type { Editor } from '@tiptap/react';
import { v4 as uuid } from 'uuid';
import { ACCORDION_NODE_NAME, ACCORDION_PANEL_NODE_NAME } from './extensions/accordion';
import { DATA_DISPLAY_NODE_NAME } from './extensions/data-display';
import { ECHARTS_NODE_NAME } from './extensions/echarts';
import { GOJS_DIAGRAM_NODE_NAME } from './extensions/gojs-diagram';
import { COLUMNS_NODE_NAME, COLUMN_NODE_NAME } from './extensions/columns';
import { KEY_POINT_CALLOUT_NODE_NAME } from './extensions/key-point-callout';
import { FORMULA_CALLOUT_NODE_NAME } from './extensions/formula-callout';
import { SIMPLE_CALLOUT_NODE_NAME } from './extensions/simple-callout';
import { WIDGET_NODE_NAME } from './extensions/widget';
import { DEFAULT_GRAPHPAD_CONFIG } from './extensions/widget/widget.model';

export const STUDY_NOTES_SLASH_MENU_ITEMS: Record<string, SlashMenuItem> = {
  formulaCallout: {
    metadata: {
      title: 'Formula Callout',
      subtext: 'Highlighted formula with explanation and optional derivation',
      keywords: ['formula', 'callout', 'equation', 'derivation', 'latex'],
      badge: FormulaIcon,
      group: 'Insert',
    },
    implementation: {
      check: (editor: Editor) => isNodeInSchema(FORMULA_CALLOUT_NODE_NAME, editor),
      action: ({ editor }: { editor: Editor }) => {
        editor
          .chain()
          .focus()
          .insertContent({
            type: FORMULA_CALLOUT_NODE_NAME,
            attrs: {
              formula: { latex_nodes: [] },
              explanation: '',
              show_derivation: false,
            },
            content: [{ type: 'paragraph' }],
          })
          .run();
      },
    },
  },
  keypointCallout: {
    metadata: {
      title: 'Key Point Callout',
      subtext: 'Highlighted key point box',
      keywords: ['keypoint', 'key point', 'callout'],
      badge: KeyIcon,
      group: 'Insert',
    },
    implementation: {
      check: (editor: Editor) => isNodeInSchema(KEY_POINT_CALLOUT_NODE_NAME, editor),
      action: ({ editor }: { editor: Editor }) => {
        editor
          .chain()
          .focus()
          .insertContent({
            type: KEY_POINT_CALLOUT_NODE_NAME,
            attrs: {
              title: 'Key Point',
              color: 'yellow',
              icon: 'key',
            },
            content: [{ type: 'paragraph' }],
          })
          .run();
      },
    },
  },
  simpleCallout: {
    metadata: {
      title: 'Simple Callout',
      subtext: 'Simple callout box with colored background',
      keywords: ['callout', 'simple callout', 'tip', 'example', 'note'],
      badge: CalloutIcon,
      group: 'Insert',
    },
    implementation: {
      check: (editor: Editor) => isNodeInSchema(SIMPLE_CALLOUT_NODE_NAME, editor),
      action: ({ editor }: { editor: Editor }) => {
        editor
          .chain()
          .focus()
          .insertContent({
            type: SIMPLE_CALLOUT_NODE_NAME,
            attrs: {
              title: 'Callout',
              variant: 'concept',
            },
            content: [{ type: 'paragraph' }],
          })
          .run();
      },
    },
  },
  twoColumns: {
    metadata: {
      title: '2 Columns',
      subtext: 'Side-by-side two-column layout',
      keywords: ['columns', '2 columns', 'two columns', 'side by side', 'layout'],
      badge: ColumnsIcon,
      group: 'Columns',
    },
    implementation: {
      check: (editor: Editor) => isNodeInSchema(COLUMNS_NODE_NAME, editor),
      action: ({ editor }: { editor: Editor }) => {
        editor
          .chain()
          .focus()
          .insertContent({
            type: COLUMNS_NODE_NAME,
            content: [
              {
                type: COLUMN_NODE_NAME,
                content: [{ type: 'paragraph' }],
              },
              {
                type: COLUMN_NODE_NAME,
                content: [{ type: 'paragraph' }],
              },
            ],
          })
          .run();
      },
    },
  },
  threeColumns: {
    metadata: {
      title: '3 Columns',
      subtext: 'Three-column layout',
      keywords: ['columns', '3 columns', 'three columns', 'layout'],
      badge: ColumnsIcon,
      group: 'Columns',
    },
    implementation: {
      check: (editor: Editor) => isNodeInSchema(COLUMNS_NODE_NAME, editor),
      action: ({ editor }: { editor: Editor }) => {
        editor
          .chain()
          .focus()
          .insertContent({
            type: COLUMNS_NODE_NAME,
            content: [
              {
                type: COLUMN_NODE_NAME,
                content: [{ type: 'paragraph' }],
              },
              {
                type: COLUMN_NODE_NAME,
                content: [{ type: 'paragraph' }],
              },
              {
                type: COLUMN_NODE_NAME,
                content: [{ type: 'paragraph' }],
              },
            ],
          })
          .run();
      },
    },
  },
  gojsDiagram: {
    metadata: {
      title: 'GoJS Diagram',
      subtext: 'Interactive GoJS diagram (tree, flowchart)',
      keywords: ['diagram', 'gojs', 'tree', 'flowchart', 'graph', 'chart'],
      badge: DiagramIcon,
      group: 'Insert',
    },
    implementation: {
      check: (editor: Editor) => isNodeInSchema(GOJS_DIAGRAM_NODE_NAME, editor),
      action: ({ editor }: { editor: Editor }) => {
        editor
          .chain()
          .focus()
          .insertContent({
            type: GOJS_DIAGRAM_NODE_NAME,
            attrs: {
              model: {
                nodes: [{ key: '1', text: 'Node 1' }],
                links: [],
              },
              layout_type: 'tree',
              height: 300,
            },
          })
          .run();
      },
    },
  },
  echarts: {
    metadata: {
      title: 'ECharts',
      subtext: 'Interactive chart (bar, line, scatter, tree, graph)',
      keywords: ['chart', 'echarts', 'bar', 'line', 'scatter', 'graph', 'visualization'],
      badge: ChartIcon,
      group: 'Insert',
    },
    implementation: {
      check: (editor: Editor) => isNodeInSchema(ECHARTS_NODE_NAME, editor),
      action: ({ editor }: { editor: Editor }) => {
        editor
          .chain()
          .focus()
          .insertContent({
            type: ECHARTS_NODE_NAME,
            attrs: {
              options: {},
              height: 400,
            },
          })
          .run();
      },
    },
  },
  dataDisplay: {
    metadata: {
      title: 'Data Display',
      subtext: 'Comma-separated data values with LaTeX rendering',
      keywords: ['data', 'display', 'values', 'dataset', 'numbers', 'list'],
      badge: DataDisplayIcon,
      group: 'Insert',
    },
    implementation: {
      check: (editor: Editor) => isNodeInSchema(DATA_DISPLAY_NODE_NAME, editor),
      action: ({ editor }: { editor: Editor }) => {
        editor
          .chain()
          .focus()
          .insertContent({
            type: DATA_DISPLAY_NODE_NAME,
            attrs: {
              data: '',
            },
          })
          .run();
      },
    },
  },
  accordion: {
    metadata: {
      title: 'Accordion',
      subtext: 'Expandable panel group',
      keywords: ['accordion', 'expansion', 'collapse', 'panels'],
      badge: AccordionIcon,
      group: 'Insert',
    },
    implementation: {
      check: (editor: Editor) => isNodeInSchema(ACCORDION_NODE_NAME, editor),
      action: ({ editor }: { editor: Editor }) => {
        editor
          .chain()
          .focus()
          .insertContent({
            type: ACCORDION_NODE_NAME,
            content: [
              {
                type: ACCORDION_PANEL_NODE_NAME,
                attrs: { title: 'Panel 1' },
                content: [{ type: 'paragraph' }],
              },
              {
                type: ACCORDION_PANEL_NODE_NAME,
                attrs: { title: 'Panel 2' },
                content: [{ type: 'paragraph' }],
              },
            ],
          })
          .run();
      },
    },
  },
  graphpad: {
    metadata: {
      title: 'Graphpad',
      subtext: 'Interactive graph widget',
      keywords: ['graphpad', 'graph', 'widget', 'chart', 'plot', 'interactive'],
      badge: GraphpadIcon,
      group: 'Insert',
    },
    implementation: {
      check: (editor: Editor) => isNodeInSchema(WIDGET_NODE_NAME, editor),
      action: ({ editor }: { editor: Editor }) => {
        editor
          .chain()
          .focus()
          .insertContent({
            type: WIDGET_NODE_NAME,
            attrs: {
              widget_type: 'graphpad',
              config: {
                ...DEFAULT_GRAPHPAD_CONFIG,
                id: `graphpad-${uuid()}`,
              },
            },
          })
          .run();
      },
    },
  },
};
